import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { unenrollFromCurriculum } from "@/lib/unenrollRpc";
import { useAuth } from "@/lib/auth";
import { useProfile } from "@/hooks/useProfile";
import { useUserRoles } from "@/hooks/useUserRoles";
import { 
  AudienceRole, 
  checkUserEligibility, 
  getEligibilityLabel, 
  getEligibilityVariant 
} from "@/hooks/useCurriculumEligibility";

export interface Curriculum {
  id: string;
  title: string;
  description: string | null;
  thumbnail_url: string | null;
  is_published: boolean;
  status?: "draft" | "preview" | "published";
  access_mode: string;
  created_at: string;
  updated_at: string;
  created_by: string | null;
  sequence_order: number;
  courses_count?: number;
  is_enrolled?: boolean;
  is_cohort_enrolled?: boolean;
  progress?: number;
  audiences?: AudienceRole[];
  eligibility_label?: string;
  eligibility_variant?: "default" | "secondary" | "destructive" | "outline";
  is_eligible?: boolean;
}

// Fetch all curricula for Learning tab - filtered by user eligibility
export function useCurricula() {
  const { user } = useAuth();
  const { data: profile } = useProfile();
  const { data: userRoles } = useUserRoles();

  return useQuery({
    queryKey: ["curricula", user?.id, profile?.role, userRoles],
    queryFn: async () => {
      const { data: curricula, error } = await supabase
        .from("curricula")
        .select("*")
        .eq("is_published", true)
        .order("sequence_order", { ascending: true });

      if (error) throw error;

      // Fetch all curriculum audiences
      const { data: audienceData } = await supabase
        .from("curriculum_audiences")
        .select("curriculum_id, audience_role");

      const audienceMap = new Map<string, AudienceRole[]>();
      (audienceData || []).forEach((a) => {
        const existing = audienceMap.get(a.curriculum_id) || [];
        audienceMap.set(a.curriculum_id, [...existing, a.audience_role as AudienceRole]);
      });

      // Filter curricula by user eligibility
      const filteredCurricula = (curricula || []).filter((curriculum) => {
        const audiences = audienceMap.get(curriculum.id);
        return checkUserEligibility(profile?.role, userRoles, audiences);
      });

      if (user) {
        // Check which curricula the user has cohort enrollments for
        const { data: cohortEnrollments } = await supabase
          .from("course_enrollments")
          .select("course_id, cohort_id")
          .eq("user_id", user.id)
          .not("cohort_id", "is", null);

        const cohortCourseIds = new Set((cohortEnrollments || []).map(e => e.course_id));

        const curriculaWithEnrollment = await Promise.all(
          filteredCurricula.map(async (curriculum) => {
            const { data: enrollment } = await supabase
              .from("curriculum_enrollments")
              .select("id")
              .eq("curriculum_id", curriculum.id)
              .eq("user_id", user.id)
              .maybeSingle();

            // Count courses via junction table
            const { data: courseLinks } = await supabase
              .from("curriculum_courses")
              .select("course_id")
              .eq("curriculum_id", curriculum.id);

            const audiences = audienceMap.get(curriculum.id);

            // Check if user has a cohort enrollment for any course in this curriculum
            const isCohortEnrolled = (courseLinks || []).some(link => cohortCourseIds.has(link.course_id));

            return {
              ...curriculum,
              is_enrolled: !!enrollment,
              is_cohort_enrolled: isCohortEnrolled,
              courses_count: courseLinks?.length || 0,
              audiences,
              eligibility_label: getEligibilityLabel(audiences),
              eligibility_variant: getEligibilityVariant(audiences),
              is_eligible: true, // All returned are eligible since we filtered
            };
          })
        );
        return curriculaWithEnrollment as Curriculum[];
      }

      // For unauthenticated users, return empty (they need to log in to see Learning)
      return [] as Curriculum[];
    },
    enabled: !!user && !!profile,
  });
}

// Fetch ALL curricula for Homepage Featured section - no filtering
export function useFeaturedCurricula() {
  const { user } = useAuth();
  const { data: profile } = useProfile();
  const { data: userRoles } = useUserRoles();

  return useQuery({
    queryKey: ["featured-curricula", user?.id],
    queryFn: async () => {
      const { data: curricula, error } = await supabase
        .from("curricula")
        .select("*")
        .eq("is_published", true)
        .order("sequence_order", { ascending: true });

      if (error) throw error;

      // Fetch all curriculum audiences
      const { data: audienceData } = await supabase
        .from("curriculum_audiences")
        .select("curriculum_id, audience_role");

      const audienceMap = new Map<string, AudienceRole[]>();
      (audienceData || []).forEach((a) => {
        const existing = audienceMap.get(a.curriculum_id) || [];
        audienceMap.set(a.curriculum_id, [...existing, a.audience_role as AudienceRole]);
      });

      // Return ALL curricula with eligibility info
      const curriculaWithEligibility = await Promise.all(
        (curricula || []).map(async (curriculum) => {
          const audiences = audienceMap.get(curriculum.id);
          const isEligible = user && profile
            ? checkUserEligibility(profile.role, userRoles, audiences)
            : false; // Not logged in = not eligible to enroll

          let isEnrolled = false;
          if (user) {
            const { data: enrollment } = await supabase
              .from("curriculum_enrollments")
              .select("id")
              .eq("curriculum_id", curriculum.id)
              .eq("user_id", user.id)
              .maybeSingle();
            isEnrolled = !!enrollment;
          }

          // Count courses via junction table
          const { data: courseLinks } = await supabase
            .from("curriculum_courses")
            .select("course_id")
            .eq("curriculum_id", curriculum.id);

          return {
            ...curriculum,
            is_enrolled: isEnrolled,
            courses_count: courseLinks?.length || 0,
            audiences,
            eligibility_label: getEligibilityLabel(audiences),
            eligibility_variant: getEligibilityVariant(audiences),
            is_eligible: isEligible,
          };
        })
      );

      return curriculaWithEligibility as Curriculum[];
    },
  });
}

export function useCurriculum(curriculumId: string) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["curriculum", curriculumId],
    queryFn: async () => {
      const { data: curriculum, error } = await supabase
        .from("curricula")
        .select("*")
        .eq("id", curriculumId)
        .maybeSingle();

      if (error) throw error;
      if (!curriculum) throw new Error("Curriculum not found");

      let isEnrolled = false;
      let isCohortEnrolled = false;

      if (user) {
        const { data: enrollment } = await supabase
          .from("curriculum_enrollments")
          .select("id")
          .eq("curriculum_id", curriculumId)
          .eq("user_id", user.id)
          .maybeSingle();

        isEnrolled = !!enrollment;

        // Count courses via junction table
        const { data: courseLinks } = await supabase
          .from("curriculum_courses")
          .select("course_id")
          .eq("curriculum_id", curriculumId);

        // Check if user has a cohort enrollment for any course in this curriculum
        if (courseLinks && courseLinks.length > 0) {
          const courseIds = courseLinks.map(link => link.course_id);
          const { data: cohortEnrollments } = await supabase
            .from("course_enrollments")
            .select("id")
            .eq("user_id", user.id)
            .in("course_id", courseIds)
            .not("cohort_id", "is", null)
            .limit(1);

          isCohortEnrolled = (cohortEnrollments?.length || 0) > 0;
        }

        return {
          ...curriculum,
          is_enrolled: isEnrolled,
          is_cohort_enrolled: isCohortEnrolled,
          courses_count: courseLinks?.length || 0,
        } as Curriculum;
      }

      // Count courses via junction table
      const { data: courseLinks } = await supabase
        .from("curriculum_courses")
        .select("course_id")
        .eq("curriculum_id", curriculumId);

      return {
        ...curriculum,
        is_enrolled: isEnrolled,
        is_cohort_enrolled: false,
        courses_count: courseLinks?.length || 0,
      } as Curriculum;
    },
    enabled: !!curriculumId,
  });
}

export interface CurriculumCourse {
  id: string;
  title: string;
  description: string | null;
  thumbnail_url: string | null;
  instructor_id: string | null;
  is_published: boolean;
  created_at: string;
  updated_at: string;
  instructor?: {
    id: string;
    full_name: string;
    avatar_url: string | null;
  } | null;
  modules_count: number;
  progress: number;
}

export function useCurriculumCourses(curriculumId: string) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["curriculum-courses", curriculumId],
    queryFn: async (): Promise<CurriculumCourse[]> => {
      // Get course IDs from junction table
      const { data: courseLinks, error: linksError } = await supabase
        .from("curriculum_courses")
        .select("course_id")
        .eq("curriculum_id", curriculumId);

      if (linksError) throw linksError;
      if (!courseLinks || courseLinks.length === 0) return [];

      const courseIds = courseLinks.map(link => link.course_id);

      const { data: courses, error } = await supabase
        .from("courses")
        .select(`
          *,
          instructor:profiles!courses_instructor_id_fkey(id, full_name, avatar_url)
        `)
        .in("id", courseIds)
        .eq("is_published", true)
        .order("created_at", { ascending: true });

      if (error) throw error;

      const coursesWithProgress = await Promise.all(
        (courses || []).map(async (course) => {
          const { data: modulesCount } = await supabase
            .from("modules")
            .select("id", { count: "exact" })
            .eq("course_id", course.id);

          let progress = 0;
          if (user) {
            const { data: progressData } = await supabase.rpc("get_course_progress", {
              _user_id: user.id,
              _course_id: course.id,
            });
            progress = progressData || 0;
          }

          return {
            ...course,
            modules_count: modulesCount?.length || 0,
            progress,
          } as CurriculumCourse;
        })
      );
      return coursesWithProgress;
    },
    enabled: !!curriculumId,
  });
}

export function useEnrollCurriculum() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (curriculumId: string) => {
      if (!user) throw new Error("Must be logged in to enroll");

      // Enroll in curriculum (use upsert to handle already enrolled case)
      const { data: curriculumEnrollment, error: curriculumError } = await supabase
        .from("curriculum_enrollments")
        .upsert(
          { curriculum_id: curriculumId, user_id: user.id },
          { onConflict: "curriculum_id,user_id", ignoreDuplicates: true }
        )
        .select()
        .maybeSingle();

      if (curriculumError) throw curriculumError;

      // Get courses via junction table
      const { data: courseLinks } = await supabase
        .from("curriculum_courses")
        .select("course_id")
        .eq("curriculum_id", curriculumId);

      // Also enroll in all courses within this curriculum
      if (courseLinks && courseLinks.length > 0) {
        // Get published courses
        const courseIds = courseLinks.map(link => link.course_id);
        const { data: publishedCourses } = await supabase
          .from("courses")
          .select("id")
          .in("id", courseIds)
          .eq("is_published", true);

        if (publishedCourses && publishedCourses.length > 0) {
          const courseEnrollments = publishedCourses.map((course) => ({
            course_id: course.id,
            user_id: user.id,
          }));

          // Use upsert to avoid duplicates
          const { error: courseEnrollError } = await supabase
            .from("course_enrollments")
            .upsert(courseEnrollments, { onConflict: "course_id,user_id", ignoreDuplicates: true });
          if (courseEnrollError) throw courseEnrollError;
        }
      }

      return curriculumEnrollment;
    },
    onSuccess: (_, curriculumId) => {
      queryClient.invalidateQueries({ queryKey: ["curricula"] });
      queryClient.invalidateQueries({ queryKey: ["curriculum", curriculumId] });
      queryClient.invalidateQueries({ queryKey: ["curriculum-courses", curriculumId] });
      queryClient.invalidateQueries({ queryKey: ["courses"] });
    },
  });
}

export interface InstructorCurriculum extends Curriculum {
  enrolled_count?: number;
}

export function useInstructorCurricula() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["instructor-curricula"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("curricula")
        .select("*")
        .order("sequence_order", { ascending: true });

      if (error) throw error;

      const curriculaWithCounts = await Promise.all(
        (data || []).map(async (curriculum) => {
          // Count courses via junction table
          const { data: courseLinks } = await supabase
            .from("curriculum_courses")
            .select("course_id")
            .eq("curriculum_id", curriculum.id);

          // Count enrolled users
          const { count: enrolledCount } = await supabase
            .from("curriculum_enrollments")
            .select("*", { count: "exact", head: true })
            .eq("curriculum_id", curriculum.id);

          return {
            ...curriculum,
            courses_count: courseLinks?.length || 0,
            enrolled_count: enrolledCount || 0,
          };
        })
      );

      return curriculaWithCounts as InstructorCurriculum[];
    },
    enabled: !!user,
  });
}

export function useCreateCurriculum() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (curriculum: { title: string; description?: string; thumbnail_url?: string }) => {
      if (!user) throw new Error("Must be logged in");

      const { data, error } = await supabase
        .from("curricula")
        .insert({ ...curriculum, created_by: user.id })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["instructor-curricula"] });
    },
  });
}

export function useUpdateCurriculum() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<Curriculum> & { id: string }) => {
      const { data, error } = await supabase
        .from("curricula")
        .update(updates)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["instructor-curricula"] });
      queryClient.invalidateQueries({ queryKey: ["curriculum", data.id] });
    },
  });
}

export function useDeleteCurriculum() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (curriculumId: string) => {
      const { error } = await supabase
        .from("curricula")
        .delete()
        .eq("id", curriculumId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["instructor-curricula"] });
      queryClient.invalidateQueries({ queryKey: ["curricula"] });
    },
  });
}

// Hook to get current course assignments for a curriculum
export function useCurriculumCourseAssignments(curriculumId: string) {
  return useQuery({
    queryKey: ["curriculum-course-assignments", curriculumId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("curriculum_courses")
        .select("*")
        .eq("curriculum_id", curriculumId);

      if (error) throw error;
      return data || [];
    },
    enabled: !!curriculumId,
  });
}

// Hook to assign courses to a curriculum
export function useAssignCoursesToCurriculum() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ curriculumId, courseIds }: { curriculumId: string; courseIds: string[] }) => {
      // Delete existing assignments
      const { error: deleteError } = await supabase
        .from("curriculum_courses")
        .delete()
        .eq("curriculum_id", curriculumId);

      if (deleteError) throw deleteError;

      // Insert new assignments
      if (courseIds.length > 0) {
        const assignments = courseIds.map(courseId => ({
          curriculum_id: curriculumId,
          course_id: courseId,
        }));

        const { error: insertError } = await supabase
          .from("curriculum_courses")
          .insert(assignments);

        if (insertError) throw insertError;
      }

      return { curriculumId, courseIds };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["instructor-curricula"] });
      queryClient.invalidateQueries({ queryKey: ["curriculum", data.curriculumId] });
      queryClient.invalidateQueries({ queryKey: ["curriculum-courses", data.curriculumId] });
      queryClient.invalidateQueries({ queryKey: ["curriculum-course-assignments", data.curriculumId] });
      queryClient.invalidateQueries({ queryKey: ["curricula"] });
    },
  });
}

export function useUnenrollCurriculum() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ curriculumId, originalEnrolledAt, progress }: { 
      curriculumId: string; 
      originalEnrolledAt?: string;
      progress?: number;
    }) => {
      if (!user) throw new Error("Must be logged in");

      // Atomic server-side unenroll. The old client-side cascade deleted the
      // learner's quiz attempts, submissions and progress FIRST, then deleted
      // the enrollments -- and course_enrollments had no DELETE policy, so that
      // step silently no-opped (200, 0 rows, error === null) while the work was
      // already gone. The RPC does everything in one transaction and raises if
      // the curriculum enrollment delete affects zero rows.
      await unenrollFromCurriculum(curriculumId);
    },
    onSuccess: (_, { curriculumId }) => {
      queryClient.invalidateQueries({ queryKey: ["curricula"] });
      queryClient.invalidateQueries({ queryKey: ["curriculum", curriculumId] });
      queryClient.invalidateQueries({ queryKey: ["curriculum-courses", curriculumId] });
      queryClient.invalidateQueries({ queryKey: ["courses"] });
      queryClient.invalidateQueries({ queryKey: ["enrolled-courses"] });
      queryClient.invalidateQueries({ queryKey: ["quiz-attempts"] });
      queryClient.invalidateQueries({ queryKey: ["assignment-submissions"] });
      queryClient.invalidateQueries({ queryKey: ["student_assignments"] });
      queryClient.invalidateQueries({ queryKey: ["homework-submissions"] });
      queryClient.invalidateQueries({ queryKey: ["my-homework-submission"] });
    },
  });
}

export function useCloneCurriculum() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (curriculumId: string) => {
      if (!user) throw new Error("Must be logged in");

      // Get the original curriculum
      const { data: original, error: fetchError } = await supabase
        .from("curricula")
        .select("*")
        .eq("id", curriculumId)
        .single();

      if (fetchError || !original) throw new Error("Curriculum not found");

      // Create a clone with "Copy of" prefix
      const { data: cloned, error: cloneError } = await supabase
        .from("curricula")
        .insert({
          title: `Copy of ${original.title}`,
          description: original.description,
          thumbnail_url: original.thumbnail_url,
          is_published: false, // Always start as draft
          access_mode: original.access_mode || 'open',
          created_by: user.id,
        })
        .select()
        .single();

      if (cloneError) throw cloneError;

      // Clone course assignments
      const { data: courseLinks } = await supabase
        .from("curriculum_courses")
        .select("course_id")
        .eq("curriculum_id", curriculumId);

      if (courseLinks && courseLinks.length > 0) {
        const newLinks = courseLinks.map(link => ({
          curriculum_id: cloned.id,
          course_id: link.course_id,
        }));
        await supabase.from("curriculum_courses").insert(newLinks);
      }

      return cloned;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["instructor-curricula"] });
      queryClient.invalidateQueries({ queryKey: ["curricula"] });
    },
  });
}

export function useReorderCurricula() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (curricula: { id: string; sequence_order: number }[]) => {
      // Use a two-step approach to avoid unique constraint violations
      // Step 1: Set all to negative values
      for (const curriculum of curricula) {
        const { error } = await supabase
          .from("curricula")
          .update({ sequence_order: -curriculum.sequence_order - 1000 })
          .eq("id", curriculum.id);
        if (error) throw error;
      }
      
      // Step 2: Set to final positive values
      for (const curriculum of curricula) {
        const { error } = await supabase
          .from("curricula")
          .update({ sequence_order: curriculum.sequence_order })
          .eq("id", curriculum.id);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["instructor-curricula"] });
      queryClient.invalidateQueries({ queryKey: ["curricula"] });
    },
  });
}
